import c from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Card, Icon, IconName, Typography } from '@repo/ui';
import {
  Accordion,
  AudienceFeedbackOverlay,
  ButtonColors,
  ButtonVariants,
  DesignButton,
} from 'components';
import { Filters } from 'components/Filters/Filters';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useActionPlanCategories from 'services/actionPlan/actionPlanCategories';
import useFeature from 'services/features/useFeature';

import emptyActionsIllustration from 'assets/images/icon/exclude.svg';
import { useAccordionOpenStates } from 'hooks/useAccordionOpenStates';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import {
  ActionPlanDesiredOutcome,
  ActionPlanItem,
  ActionPlanOutcomesCategory,
} from 'types/ActionPlan';
import { ActionPlanItemCard } from './ActionPlanGroupV2/ActionPlanGroupV2.js';
import RetestStep from './RetestStep/RetestStep';

import styles from './ActionPlanCategories.module.css';

const ActionFilter = ({
  iconName,
  display,
}: {
  iconName: IconName;
  display: string;
}) => (
  <div className='flex gap-1'>
    <Icon name={iconName} size='s' />
    <Typography variant='label'>{display}</Typography>
  </div>
);

const actionFilterConfig = [
  {
    value: 'DietarySuggestions',
    display: <ActionFilter iconName='carrot' display='Diet' />,
  },
  {
    value: 'LifestyleSuggestions',
    display: <ActionFilter iconName='heartHealth' display='Lifestyle' />,
  },
  {
    value: 'SupplementSuggestions',
    display: <ActionFilter iconName='pill' display='Supplement' />,
  },
];

type OutcomeGroup = {
  outcome: ActionPlanDesiredOutcome;
  actions: ActionPlanItem[];
};

type StepResult = {
  outcomeGroups: OutcomeGroup[];
  hiddenByFiltersCount: number;
};

const buildOutcomeGroupsForStep = (
  rows: ActionPlanOutcomesCategory[],
  seenActionIds: Set<number>,
  actionFilters: string[],
): StepResult => {
  const byOutcomeKey = new Map<string, OutcomeGroup>();
  const filteredOutActionIds = new Set<number>();

  rows.forEach(row => {
    const outcome = row.desired_outcomes;
    const rec = outcome?.action_plan_recommendation as
      | (ActionPlanItem & { category?: string })
      | undefined;

    if (!outcome || !rec) return;

    const recId = rec.id as number | undefined;
    if (recId == null) return;

    if (seenActionIds.has(recId)) return;
    seenActionIds.add(recId);

    if (
      actionFilters.length > 0 &&
      rec.category &&
      !actionFilters.includes(rec.category)
    ) {
      filteredOutActionIds.add(recId);
      return;
    }

    const metricId = outcome?.gut_vaginal_health?.id ?? 'no-metric';
    const outcomeTitle = outcome.title ?? '';
    const outcomeKey = `${metricId}-${outcomeTitle}`;

    if (!byOutcomeKey.has(outcomeKey)) {
      byOutcomeKey.set(outcomeKey, {
        outcome,
        actions: [],
      });
    }

    const group = byOutcomeKey.get(outcomeKey)!;
    const alreadyHasAction = group.actions.some(a => a.id === rec.id);
    if (!alreadyHasAction) {
      group.actions.push(rec);
    }
  });

  const nonEmptyGroups = Array.from(byOutcomeKey.values()).filter(
    group => group.actions.length > 0,
  );

  const sortedGroups = nonEmptyGroups.sort((a, b) => {
    const orderA = a.outcome.outcome_display_order ?? 0;
    const orderB = b.outcome.outcome_display_order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return (a.outcome.title ?? '').localeCompare(b.outcome.title ?? '');
  });

  sortedGroups.forEach(group => {
    group.actions.sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );
  });

  return {
    outcomeGroups: sortedGroups,
    hiddenByFiltersCount: filteredOutActionIds.size,
  };
};

const ActionPlanCategories = ({ kitId }: { kitId?: string | null }) => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const effectiveKitId = kitId ?? currentKitId;
  const { data, isSuccess } = useActionPlanCategories({
    kitId: effectiveKitId,
  });
  const { isFeatureEnabled: isApV5FeedbackEnabled } =
    useFeature('ap_v5_feedback');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [actionFilters, setActionFilters] = useState<string[]>([]);

  useEffect(() => {
    //activate the feedback overlay after 15 seconds
    if (!isApV5FeedbackEnabled || !effectiveKitId || !isSuccess) return;

    const t = window.setTimeout(() => {
      setIsFeedbackOpen(true);
    }, 15000); // 15 seconds

    return () => {
      window.clearTimeout(t);
    };
  }, [effectiveKitId, isSuccess, isFeedbackOpen, isApV5FeedbackEnabled]);

  const steps = useMemo(() => {
    const rows = (data ?? []) as ActionPlanOutcomesCategory[];

    const map = new Map<
      number,
      {
        title: string;
        rows: ActionPlanOutcomesCategory[];
      }
    >();

    rows.forEach(row => {
      const stepNumber = row.action_category_number ?? 0;
      const title = row.action_category_text ?? '';

      if (!map.has(stepNumber)) {
        map.set(stepNumber, { title, rows: [] });
      }
      map.get(stepNumber)!.rows.push(row);
    });

    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [data]);

  const stepAccordionIds = useMemo(
    () => steps.map(([stepNumber]) => `step-${stepNumber}`),
    [steps],
  );
  const defaultStepOpen = useCallback((id: string) => id === 'step-1', []);
  const { accordionOpenStates, handleAccordionToggle } = useAccordionOpenStates({
    storageKey: effectiveKitId
      ? `action-plan-accordion-states-${effectiveKitId}`
      : null,
    accordionIds: stepAccordionIds,
    defaultOpen: defaultStepOpen,
  });

  if (!effectiveKitId) return null;

  const shouldShowEmptyActionsState = steps.length === 1;

  const renderedSteps = useMemo(() => {
    if (!effectiveKitId) return [];

    const seenActionIds = new Set<number>();

    const stepsJsx = steps.map(([stepNumber, { title, rows }]) => {
      const { outcomeGroups, hiddenByFiltersCount } = buildOutcomeGroupsForStep(
        rows,
        seenActionIds,
        actionFilters,
      );

      const stepKey = `step-${stepNumber}`;
      const isStepOpen = accordionOpenStates[stepKey] ?? stepNumber === 1;

      return (
        <Card variant='wrapper' key={`step-${stepNumber}`}>
          <div className={styles.card}>
            <Accordion
              header={
                <Typography variant='heading-l' className={styles.headerTitle}>
                  {title || `Step ${stepNumber}`}
                </Typography>
              }
              initialState={isStepOpen}
              isOpen={isStepOpen}
              onToggle={updatedIsOpen => {
                trackEvent('Toggled Accordion', {
                  accordion_group: 'action_plan_step',
                  accordion_id: `action_plan_step_${stepNumber}`,
                  accordion_title: title || `Step ${stepNumber}`,
                  state: updatedIsOpen ? 'expanded' : 'collapsed',
                  variant: 'v5_action_plan_page',
                });
                handleAccordionToggle(stepKey, updatedIsOpen);
              }}
            >
              <div className={styles.content}>
                {outcomeGroups.length > 0 && (
                  <div>
                    {outcomeGroups.map(group => {
                      const outcome = group.outcome;
                      const actions = group.actions;

                      const evaluationColor =
                        outcome?.gut_vaginal_health
                          ?.gut_vaginal_health_kits?.[0]?.evaluation_color ??
                        (outcome?.section === 'Condition' ? 'yellow' : 'grey');

                      const metricId = outcome?.gut_vaginal_health?.id;
                      const isMetricLink =
                        !!metricId && outcome?.section === 'Gut Vaginal Health';

                      const outcomeKey = `outcome-${outcome.id}-${metricId ?? 'no-metric'}`;

                      return (
                        <div key={outcomeKey} className={styles.outcome}>
                          <div
                            className={c(
                              styles.outcomePill,
                              styles[
                                evaluationColor as
                                  | 'red'
                                  | 'orange'
                                  | 'yellow'
                                  | 'green'
                                  | 'grey'
                              ] ?? styles.grey,
                              isMetricLink && styles.clickable,
                            )}
                            onClick={() => {
                              if (isMetricLink && metricId && effectiveKitId) {
                                const href = `/results/${effectiveKitId}/insights/all-insights/${metricId}?evaluations=red%2Corange%2Cgreen%2Cgrey`;

                                trackEvent('Clicked Action Plan Outcome', {
                                  outcome_title: outcome.title,
                                  metric_id: metricId,
                                  href,
                                  variant: 'v5_action_plan_page',
                                  accordion_id: `action_plan_step_${stepNumber}`,
                                });

                                navigate(href, {
                                  state: { from: pathname },
                                });
                              }
                            }}
                          >
                            <Typography variant='label'>
                              {outcome.title}
                            </Typography>
                            {isMetricLink && <Icon name='trendUp02' size='s' />}
                          </div>

                          {actions.map(action => (
                            <ActionPlanItemCard
                              key={`action-${action.id}`}
                              action={action}
                              kitId={effectiveKitId}
                              trackingVariant={'v5_action_plan_page'}
                              accordionId={`action_plan_step_${stepNumber}`}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {hiddenByFiltersCount > 0 && (
                  <div className={styles.clearFilters}>
                    <Typography variant='label' className='my-auto'>
                      {`${hiddenByFiltersCount} actions hidden by filters`}
                    </Typography>
                    <DesignButton
                      label='Clear Filters'
                      iconNameLeft='closeSm'
                      onClick={() => setActionFilters([])}
                      variant={ButtonVariants.TEXT}
                      color={ButtonColors.DARK}
                      className={styles.clearFiltersButton}
                    />
                  </div>
                )}
              </div>
            </Accordion>
          </div>
        </Card>
      );
    });

    return stepsJsx;
  }, [
    steps,
    actionFilters,
    effectiveKitId,
    pathname,
    trackEvent,
    navigate,
    accordionOpenStates,
    handleAccordionToggle,
  ]);

  return (
    <div>
      {isApV5FeedbackEnabled && (
        <AudienceFeedbackOverlay
          kitId={effectiveKitId}
          isOpen={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          feedbackType='ap_v5'
          questions={['ActionPlanQuestion', 'NpsQuestion', 'NpsTextQuestion']}
        />
      )}
      <Filters
        name='categories'
        filters={actionFilters}
        filterConfig={actionFilterConfig}
        setFiltered={async (next: string[]) => {
          const newlyAdded = next.filter(f => !actionFilters.includes(f));
          newlyAdded.forEach(filter => {
            const filterType =
              filter === 'DietarySuggestions'
                ? 'diet'
                : filter === 'LifestyleSuggestions'
                  ? 'lifestyle'
                  : filter === 'SupplementSuggestions'
                    ? 'supplement'
                    : null;
            if (filterType) {
              trackEvent('Action Plan Filter Applied', {
                filter_type: filterType,
              });
            }
          });
          setActionFilters(next);
        }}
      />
      {shouldShowEmptyActionsState && (
        <Card variant='wrapper' className='mt-3'>
          <div className={styles.emptyActionsState}>
            <img src={emptyActionsIllustration} alt='empty actions' />
            <div className='flex flex-column gap-1'>
              <Typography variant='heading-l' className='black'>
                No actions right now
              </Typography>
              <Typography variant='body-s' color='dark'>
                {`If you’re experiencing symptoms, there may be more to the story than your gut microbiome. Factors like genetics and your environment can also play a role.`}
              </Typography>
            </div>
          </div>
        </Card>
      )}
      <div className={styles.cardList}>
        {renderedSteps}
        {!shouldShowEmptyActionsState && effectiveKitId && (
          <RetestStep kitId={effectiveKitId} stepNumber={steps.length} />
        )}
      </div>
    </div>
  );
};

export default ActionPlanCategories;
